import React from 'react';
import Icon from '../../../components/AppIcon';
import { motion } from 'framer-motion';

const ProcessStepsSection = ({ currentLanguage }) => {
  const content = {
    EN: {
      title: "Detailed Process",
      subtitle: "Everything you need to know about getting started",
      steps: [
        {
          icon: "FileText",
          title: "Application",
          description: "Fill out our secure online form",
          details: [
            "Basic personal information required",
            "Valid Canadian ID needed",
            "Instant eligibility check",
            "No credit check required"
          ]
        },
        {
          icon: "Mail",
          title: "Card Delivery",
          description: "Receive your card in 7-10 business days",
          details: [
            "Shipped to your verified address",
            "Secure packaging and delivery",
            "Track your shipment online",
            "Activation instructions included"
          ]
        },
        {
          icon: "Smartphone",
          title: "Activation & Setup",
          description: "Activate your card and set up your account",
          details: [
            "Call activation number or use app",
            "Set up online account access",
            "Load funds to your card",
            "Start using immediately"
          ]
        },
        {
          icon: "TrendingUp",
          title: "Credit Building",
          description: "Your activity may be reported to Equifax",
          details: [
            "Monthly reporting of account activity",
            "Positive payment history tracking",
            "Responsible usage patterns",
            "Results may vary by individual"
          ]
        }
      ]
    },
    FR: {
      title: "Processus détaillé",
      subtitle: "Tout ce que vous devez savoir pour commencer",
      steps: [
        {
          icon: "FileText",
          title: "Application",
          description: "Remplissez notre formulaire en ligne sécurisé",
          details: [
            "Informations personnelles de base requises",
            "Pièce d\'identité canadienne valide nécessaire",
            "Vérification d'admissibilité instantanée",
            "Aucune vérification de crédit requise"
          ]
        },
        {
          icon: "Mail",
          title: "Livraison de carte",
          description: "Recevez votre carte en 7-10 jours ouvrables",
          details: [
            "Expédié à votre adresse vérifiée",
            "Emballage et livraison sécurisés",
            "Suivez votre expédition en ligne",
            "Instructions d\'activation incluses"
          ]
        },
        {
          icon: "Smartphone",
          title: "Activation et configuration",
          description: "Activez votre carte et configurez votre compte",
          details: [
            "Appelez le numéro d\'activation ou utilisez l\'application",
            "Configurez l\'accès au compte en ligne",
            "Chargez des fonds sur votre carte",
            "Commencez à utiliser immédiatement"
          ]
        },
        {
          icon: "TrendingUp",
          title: "Construction de crédit",
          description: "Votre activité peut être signalée à Equifax",
          details: [
            "Rapport mensuel de l\'activité du compte",
            "Suivi de l'historique de paiement positif",
            "Modèles d\'utilisation responsables",
            "Les résultats peuvent varier selon l\'individu"
          ]
        }
      ]
    }
  };

  const text = content?.[currentLanguage] || content?.EN;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {text?.title}
          </h2>
          <p className="text-xl text-gray-600">
            {text?.subtitle}
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {text?.steps?.map((step, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white rounded-2xl p-6 shadow-card hover:shadow-lg transition-shadow duration-300"
            >
              {/* Step Number */}
              <div className="flex items-center justify-between mb-6">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <Icon name={step?.icon} size={24} className="text-white" />
                </div>
                <div className="text-2xl font-bold text-gray-300">
                  {String(index + 1)?.padStart(2, '0')}
                </div>
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {step?.title}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {step?.description}
              </p>

              {/* Details */}
              <ul className="space-y-2">
                {step?.details?.map((detail, detailIndex) => (
                  <li key={detailIndex} className="flex items-start space-x-2 text-sm text-gray-500">
                    <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessStepsSection;